import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./EditProfile.css";
import Button from "../../components/Button/Button3";
import PhoneNumberField, {
  buildPhoneNumber,
  isValidPhoneNumber,
  splitPhoneNumber,
} from "../PhoneNumberField/PhoneNumberField";
import { useConfirmDialog } from "../ConfirmDialog/ConfirmDialogProvider";

const POSITION_OPTIONS = [
  { value: "Unknown", label: "Not specified" },
  { value: "Attacker", label: "Attacker" },
  { value: "Midfielder", label: "Midfielder" },
  { value: "Defender", label: "Defender" },
  { value: "Keeper", label: "Keeper" },
];

function ThemedSelect({ value, options, onChange, ariaLabel }) {
  const wrapperRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen]);

  return (
    <div className="edit-profile-select-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className="edit-profile-select-trigger"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        <span className="edit-profile-select-value">{selectedOption?.label || ""}</span>
        <span
          className={`edit-profile-select-chevron${isOpen ? " is-open" : ""}`}
          aria-hidden="true"
        >
          v
        </span>
      </button>

      {isOpen && (
        <div className="edit-profile-select-menu" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`edit-profile-select-option${option.value === value ? " is-selected" : ""}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EditProfile({ onEditSuccess }) {
  const [firstName, setFirstName]   = useState("");
  const [lastName, setLastName]     = useState("");
  const [phoneCode, setPhoneCode] = useState("+1");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [position, setPosition]     = useState("Unknown");
  const [email, setEmail]           = useState("");
  const [newEmail, setNewEmail]     = useState("");
  const [confirmNewEmail, setConfirmNewEmail] = useState("");
  const [authProvider, setAuthProvider] = useState("local");
  const [emailConfirmationRequired, setEmailConfirmationRequired] = useState(false);
  const [emailConfirmationCode, setEmailConfirmationCode] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [pendingEmailTarget, setPendingEmailTarget] = useState("");
  const [message, setMessage]       = useState("");
  const [messageType, setMessageType] = useState("success");
  const [loading, setLoading]       = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendingDeleteCode, setIsSendingDeleteCode] = useState(false);
  const [deleteCodeRequested, setDeleteCodeRequested] = useState(false);
  const [deleteCode, setDeleteCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [isResendingEmailCode, setIsResendingEmailCode] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteMessageType, setDeleteMessageType] = useState("success");
  const confirm = useConfirmDialog();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const idToken = localStorage.getItem("idToken");
        if (!idToken) {
          setMessageType("error");
          setMessage("You must be logged in to view your profile.");
          return;
        }

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/profile/user-profile`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        const {
          first_name,
          last_name,
          email,
          phone_number,
          position,
          authProvider,
        } = response.data;
        const parsedPhoneNumber = splitPhoneNumber(phone_number || "");
        setFirstName(first_name || "");
        setLastName(last_name || "");
        setPhoneCode(parsedPhoneNumber.dialCode);
        setPhoneDigits(parsedPhoneNumber.phoneDigits);
        setPosition(position || "Unknown");
        setEmail(email || "");
        setAuthProvider(authProvider || "local");
      } catch (error) {
        setMessageType("error");
        setMessage("Failed to load profile data.");
        console.error(error);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (emailResendCooldown <= 0) return;

    const timer = setInterval(() => {
      setEmailResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [emailResendCooldown]);

  const handleSaveChanges = async () => {
    setMessage("");
    setMessageType("success");
    setLoading(true);

    let finalEmail = email;
    if (newEmail.trim()) {
      if (newEmail !== confirmNewEmail) {
        setMessageType("error");
        setMessage("New emails do not match.");
        setLoading(false);
        return;
      }
      finalEmail = newEmail;
    }

    if (phoneDigits.trim() && !isValidPhoneNumber(phoneCode, phoneDigits)) {
      setMessageType("error");
      setMessage("Enter a valid phone number.");
      setLoading(false);
      return;
    }

    try {
      const idToken = localStorage.getItem("idToken");
      if (!idToken) {
        setMessageType("error");
        setMessage("You must be logged in to update your profile.");
        setLoading(false);
        return;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/profile/user-profile`,
        {
          firstName,
          lastName,
          phoneNumber: buildPhoneNumber(phoneCode, phoneDigits),
          position,
          email: finalEmail,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (response.data?.emailConfirmationRequired) {
        setPendingEmailTarget(finalEmail);
        setEmailConfirmationRequired(true);
        setEmailConfirmationCode("");
        setCurrentPassword("");
        setEmailResendCooldown(30);
        setMessageType("success");
        setMessage(
          response.data.message ||
            "Please confirm your new email with the code we sent and check your spam folder."
        );
        return;
      }

      if (newEmail.trim()) {
        setEmail(finalEmail);
        setNewEmail("");
        setConfirmNewEmail("");
      }

      setEmailConfirmationRequired(false);
      setEmailConfirmationCode("");
      setCurrentPassword("");
      setPendingEmailTarget("");
      setMessageType("success");
      setMessage("Profile updated successfully!");

      const updatedProfileResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/profile/user-profile`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      
      // Exit edit mode
      if (typeof onEditSuccess === 'function') {
        onEditSuccess(updatedProfileResponse.data);
      }
    } catch (error) {
      setMessageType("error");
      setMessage(error.response?.data?.error || "Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEmailConfirmationRequired(false);
    setEmailConfirmationCode("");
    setCurrentPassword("");
    setPendingEmailTarget("");
    setEmailResendCooldown(0);
    // Exit edit mode without saving
    if (typeof onEditSuccess === 'function') {
      onEditSuccess();
    }
  };

  const handleResendEmailChangeCode = async () => {
    if (isResendingEmailCode || emailResendCooldown > 0) return;

    setIsResendingEmailCode(true);
    setMessage("");
    setMessageType("success");

    try {
      const idToken = localStorage.getItem("idToken");
      if (!idToken) {
        setMessageType("error");
        setMessage("You must be logged in to resend the confirmation code.");
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/profile/resend-email-change-code`,
        {},
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      setEmailResendCooldown(30);
      setMessageType("success");
      setMessage(
        response.data?.message ||
          "A new confirmation code was sent to your email. Please check your inbox and spam folder."
      );
    } catch (error) {
      setMessageType("error");
      setMessage(error.response?.data?.error || "Failed to resend email confirmation code.");
    } finally {
      setIsResendingEmailCode(false);
    }
  };

  const handleConfirmEmailChange = async () => {
    const code = emailConfirmationCode.trim();
    if (!code) {
      setMessageType("error");
      setMessage("Please enter the confirmation code.");
      return;
    }
    if (authProvider === "local" && !currentPassword) {
      setMessageType("error");
      setMessage("Please enter your current password.");
      return;
    }

    setLoading(true);
    setMessage("");
    setMessageType("success");

    try {
      const idToken = localStorage.getItem("idToken");
      if (!idToken) {
        setMessageType("error");
        setMessage("You must be logged in to confirm your email.");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/profile/confirm-email-change`,
        {
          confirmationCode: code,
          ...(authProvider === "local" ? { currentPassword } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      const nextIdToken = response.data?.idToken || idToken;
      if (response.data?.idToken && response.data?.accessToken) {
        storeAuthTokens(response.data);
      }

      const confirmedEmail = response.data?.email || pendingEmailTarget || email;
      setEmail(confirmedEmail);
      setNewEmail("");
      setConfirmNewEmail("");
      setEmailConfirmationCode("");
      setCurrentPassword("");
      setEmailConfirmationRequired(false);
      setPendingEmailTarget("");
      setEmailResendCooldown(0);
      setMessageType("success");
      setMessage(response.data?.message || "Email updated successfully.");

      if (response.data?.requiresReauth && !response.data?.idToken) {
        clearAuthStorage();
        window.location.assign("/login");
        return;
      }

      const updatedProfileResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/profile/user-profile`,
        {
          headers: {
            Authorization: `Bearer ${nextIdToken}`,
          },
        }
      );

      if (typeof onEditSuccess === 'function') {
        onEditSuccess(updatedProfileResponse.data);
      }
    } catch (error) {
      setMessageType("error");
      setMessage(error.response?.data?.error || "Failed to confirm email change.");
    } finally {
      setLoading(false);
    }
  };

  const clearAuthStorage = () => {
    localStorage.removeItem("idToken");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("profilePictureVersion");
  };

  const storeAuthTokens = ({ idToken, accessToken, refreshToken }) => {
    if (idToken) localStorage.setItem("idToken", idToken);
    if (accessToken) localStorage.setItem("accessToken", accessToken);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
  };

  const isEmailConfirmationStep = emailConfirmationRequired;

  const sendDeleteCodeRequest = async () => {
    if (isSendingDeleteCode || isDeleting || resendCooldown > 0) return;

    setDeleteMessage("");
    setDeleteMessageType("success");
    setIsSendingDeleteCode(true);

    try {
      const idToken = localStorage.getItem("idToken");
      if (!idToken) {
        setDeleteMessageType("error");
        setDeleteMessage("You must be logged in to delete your account.");
        setIsSendingDeleteCode(false);
        return;
      }

      await axios.post(`${process.env.REACT_APP_API_URL}/profile/delete-account/request-code`, {}, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      setDeleteCodeRequested(true);
      setResendCooldown(30);
      setDeleteMessageType("success");
      setDeleteMessage("A deletion code was sent to your email. Please check your inbox and spam folder.");
    } catch (error) {
      setDeleteMessageType("error");
      setDeleteMessage(error.response?.data?.error || "Failed to send deletion code.");
    } finally {
      setIsSendingDeleteCode(false);
    }
  };

  const handleRequestDeleteCode = async () => {
    if (isSendingDeleteCode || isDeleting || resendCooldown > 0) return;

    if (!deleteCodeRequested) {
      const sendCodeConfirmed = await confirm({
        title: "DELETE ACCOUNT",
        message: "We will send a confirmation code to your email. Please check your spam folder if needed. Continue?",
        confirmText: "Send Code",
        cancelText: "Cancel",
      });
      if (!sendCodeConfirmed) return;
      await sendDeleteCodeRequest();
      return;
    }

    await sendDeleteCodeRequest();
  };

  const handleDeleteAccount = async () => {
    if (isDeleting) return;

    const trimmedCode = deleteCode.trim();
    if (!trimmedCode) {
      setDeleteMessageType("error");
      setDeleteMessage("Please enter the deletion code sent to your email. Check your inbox and spam folder.");
      return;
    }

    const confirmed = await confirm({
      title: "DELETE ACCOUNT",
      message: "Are you sure you want to permanently delete your account?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });
    if (!confirmed) return;

    setDeleteMessage("");
    setDeleteMessageType("error");
    setIsDeleting(true);

    try {
      const idToken = localStorage.getItem("idToken");
      if (!idToken) {
        setDeleteMessage("You must be logged in to delete your account.");
        setIsDeleting(false);
        return;
      }

      await axios.delete(`${process.env.REACT_APP_API_URL}/profile/delete-account`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        data: {
          confirmationCode: trimmedCode,
        },
      });

      clearAuthStorage();
      window.location.assign("/login");
    } catch (error) {
      setDeleteMessage(error.response?.data?.error || "Failed to delete account.");
      setIsDeleting(false);
    }
  };

  return (
    <div className="edit-profile-overlay" role="dialog" aria-modal="true" aria-label="Edit your profile">
      <div className="edit-profile-form-container">
        <p className="edit-profile-title">
          {isEmailConfirmationStep ? "CONFIRM YOUR NEW EMAIL" : "EDIT YOUR PROFILE"}
        </p>
        <form className="edit-profile-form" onSubmit={(e) => e.preventDefault()}>
          {isEmailConfirmationStep ? (
            <div className="edit-profile-confirmation-step">
              <p className="edit-profile-confirmation-copy">
                Enter the code sent to <strong>{pendingEmailTarget || newEmail || email}</strong>.
              </p>
              {authProvider === "local" && (
                <p className="edit-profile-confirmation-copy">
                  Confirming a local account email change also requires your current password.
                </p>
              )}
              <div className="edit-profile-field">
                <span>Email Confirmation Code:</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={emailConfirmationCode}
                  onChange={(e) => setEmailConfirmationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="edit-profile-input-line"
                />
              </div>
              {authProvider === "local" && (
                <div className="edit-profile-field">
                  <span>Current Password:</span>
                  <input
                    type="password"
                    placeholder="Enter your current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="edit-profile-input-line"
                  />
                </div>
              )}
              <div className="edit-profile-resend-row">
                <button
                  type="button"
                  className="edit-profile-resend-link"
                  onClick={handleResendEmailChangeCode}
                  disabled={isResendingEmailCode || emailResendCooldown > 0}
                >
                  {isResendingEmailCode
                    ? "Sending..."
                    : emailResendCooldown > 0
                      ? `Resend Code (${emailResendCooldown}s)`
                      : "Resend Code"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="edit-profile-field">
                <span>First Name:</span>
                <input
                  type="text"
                  placeholder="Enter your first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="edit-profile-input-line"
                />
              </div>
              <div className="edit-profile-field">
                <span>Last Name:</span>
                <input
                  type="text"
                  placeholder="Enter your last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="edit-profile-input-line"
                />
              </div>
              <div className="edit-profile-field">
                <span>Current Email:</span>
                <input
                  type="email"
                  placeholder="Your current email"
                  value={email}
                  disabled
                  className="edit-profile-input-line"
                />
              </div>
              <div className="edit-profile-field">
                <span>Phone:</span>
                <PhoneNumberField
                  dialCode={phoneCode}
                  phoneDigits={phoneDigits}
                  onDialCodeChange={setPhoneCode}
                  onPhoneDigitsChange={setPhoneDigits}
                />
              </div>
              <div className="edit-profile-field">
                <span>Position:</span>
                <ThemedSelect
                  value={position}
                  options={POSITION_OPTIONS}
                  onChange={setPosition}
                  ariaLabel="Select position"
                />
              </div>
              <div className="edit-profile-field">
                <span>New Email (optional):</span>
                <input
                  type="email"
                  placeholder="Enter your new email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setEmailConfirmationRequired(false);
                    setEmailConfirmationCode("");
                    setCurrentPassword("");
                    setPendingEmailTarget("");
                    setEmailResendCooldown(0);
                  }}
                  className="edit-profile-input-line"
                />
              </div>
              {newEmail.trim() && (
                <div className="edit-profile-field">
                  <span>Confirm New Email:</span>
                  <input
                    type="email"
                    placeholder="Confirm your new email"
                    value={confirmNewEmail}
                    onChange={(e) => {
                      setConfirmNewEmail(e.target.value);
                      setEmailConfirmationRequired(false);
                      setEmailConfirmationCode("");
                      setCurrentPassword("");
                      setPendingEmailTarget("");
                      setEmailResendCooldown(0);
                    }}
                    className="edit-profile-input-line"
                  />
                </div>
              )}
            </>
          )}

          {message && (
            <p className={`edit-profile-message ${messageType === "error" ? "error" : "success"}`}>
              {message}
            </p>
          )}

          <div className="edit-profile-button-container">
            {isEmailConfirmationStep ? (
              <Button
                variant="small"
                primaryText={loading ? "Confirming..." : "Confirm Email"}
                onClick={handleConfirmEmailChange}
              />
            ) : (
              <Button
                variant="small"
                primaryText={loading ? "Saving..." : "Update"}
                onClick={handleSaveChanges}
              />
            )}
            <Button
              variant="small"
              primaryText="Cancel"
              onClick={handleCancel}
            />
          </div>

          {!isEmailConfirmationStep && (
            <div className="delete-account-wrapper">
              {!deleteCodeRequested && (
                <button
                  type="button"
                  className="delete-account-link"
                  onClick={handleRequestDeleteCode}
                  disabled={isSendingDeleteCode || isDeleting}
                >
                  {isSendingDeleteCode ? "Sending code..." : "Delete Account"}
                </button>
              )}
              <p className="delete-account-text">This action is permanent.</p>
              {!deleteCodeRequested && deleteMessage && (
                <p className={`delete-account-message ${deleteMessageType === "error" ? "error" : "success"}`}>
                  {deleteMessage}
                </p>
              )}

              {deleteCodeRequested && (
                <div className="delete-account-confirmation">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    value={deleteCode}
                    onChange={(e) => setDeleteCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="edit-profile-input-line delete-code-input"
                  />
                  <div className="delete-account-action-row">
                    <button
                      type="button"
                      className="delete-account-confirm-link"
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting account..." : "Confirm Delete"}
                    </button>
                    <button
                      type="button"
                      className="delete-account-resend-link"
                      onClick={handleRequestDeleteCode}
                      disabled={isSendingDeleteCode || isDeleting || resendCooldown > 0}
                    >
                      {isSendingDeleteCode
                        ? "Sending..."
                        : resendCooldown > 0
                          ? `Resend (${resendCooldown}s)`
                          : "Resend Code"}
                    </button>
                  </div>
                  {deleteMessage && (
                    <p className={`delete-account-message ${deleteMessageType === "error" ? "error" : "success"}`}>
                      {deleteMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default EditProfile;
